import React, { useEffect, useState } from "react";
import {
    Button, TextField, MenuItem, IconButton, Tooltip, Autocomplete, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import moment from "moment";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import { toast } from "react-toastify";
import SkeletonLoader from "../../loaders/Skeleton";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { CYCLE_STATUS } from "./helpers";
import "./Performance.scss";

const blankCategory = () => ({ name: "", weight: 0, kras: [{ title: "", metric: "" }] });

const blankTemplate = () => ({ name: "", description: "", appliesTo: [], categories: [blankCategory()] });

const Configure = () => {

    const { fetchData } = useAxios();

    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [busy, setBusy] = useState(false);

    const [form, setForm] = useState(blankTemplate());
    const [cycleForm, setCycleForm] = useState({
        year: new Date().getFullYear(), quarter: 1, template: "", templates: [],
    });

    /* the department and designation values people actually have, so an
       audience is picked from what is on record rather than guessed */
    const [audiences, setAudiences] = useState({ departments: [], designations: [], headcount: 0 });
    const [preview, setPreview] = useState(null);

    const load = async () => {
        setLoading(true);
        const [t, c, a] = await Promise.all([
            fetchData({ url: URLS.reviewTemplates, method: "get" }),
            fetchData({ url: URLS.reviewCycles, method: "get" }),
            fetchData({ url: URLS.reviewAudiences, method: "get" }),
        ]);
        setLoading(false);
        setTemplates(t?.data || []);
        setCycles(c?.data || []);
        if (a?.data) setAudiences(a.data);
        if (!cycleForm.template && t?.data?.length) {
            setCycleForm((prev) => ({ ...prev, template: t.data[0]._id }));
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    /* the weights are meant to add up to 100 — show the total rather than
       silently correcting what an admin typed */
    const totalWeight = form.categories.reduce((t, c) => t + (Number(c.weight) || 0), 0);
    const remainingWeight = 100 - totalWeight;

    const editCategory = (i, patch) => {
        const categories = [...form.categories];
        categories[i] = { ...categories[i], ...patch };
        setForm({ ...form, categories });
    };

    const editKra = (ci, ki, patch) => {
        const categories = [...form.categories];
        const kras = [...categories[ci].kras];
        kras[ki] = { ...kras[ki], ...patch };
        categories[ci] = { ...categories[ci], kras };
        setForm({ ...form, categories });
    };

    const saveTemplate = async () => {
        if (busy) return;

        /* the same rule the API enforces, said before the round trip */
        if (totalWeight !== 100) {
            toast.error(`Category weights must add up to 100% — they currently total ${totalWeight}%`);
            return;
        }

        setBusy(true);
        const resp = await fetchData({ url: URLS.reviewTemplates, method: "post", body: form });
        setBusy(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not save the template");
            return;
        }
        toast.success(resp?.message || "Saved");
        setForm(blankTemplate());
        load();
    };

    /* every value on record, offered once, with how many people carry it */
    const audienceOptions = [
        ...audiences.departments.map((d) => ({ ...d, kind: "Department" })),
        ...audiences.designations.map((d) => ({ ...d, kind: "Designation" })),
    ];

    /* How many people a set of words catches — counted over the people
       themselves, not over the two tallies, so somebody whose department and
       designation both match is still one person. Same whole-word rule the
       API uses, so this number is the one the rollout will produce. */
    const wordMatches = (words) => {
        if (!words.length) return audiences.headcount;

        const escape = (w) => String(w).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const tests = words.map((w) => new RegExp(`(^|[^a-z0-9])${escape(w)}([^a-z0-9]|$)`, "i"));

        return (audiences.people || []).filter((p) => {
            const haystack = `${p.department || ""} ${p.designation || ""}`;
            return tests.some((re) => re.test(haystack));
        }).length;
    };

    /* load an existing template back into the builder */
    const editTemplate = (t) => {
        setForm({
            _id: t._id,
            name: t.name,
            description: t.description || "",
            appliesTo: t.appliesTo || [],
            categories: (t.categories || []).map((c) => ({
                name: c.name,
                weight: c.weight || 0,
                kras: (c.kras || []).map((k) => ({ title: k.title, metric: k.metric || "" })),
            })),
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* who would get what, read from the same plan the launch uses */
    const showPreview = async (cycle) => {
        setBusy(true);
        const resp = await fetchData({
            url: `${URLS.reviewCycles}/${cycle._id}/preview`, method: "get",
        });
        setBusy(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not work out the rollout");
            return;
        }
        setPreview({ cycle, ...resp.data });
    };

    const moveTemplate = (index, by) => {
        const next = [...cycleForm.templates];
        const to = index + by;
        if (to < 0 || to >= next.length) return;
        [next[index], next[to]] = [next[to], next[index]];
        setCycleForm({ ...cycleForm, templates: next });
    };

    const saveCycle = async () => {
        if (busy) return;
        setBusy(true);
        const resp = await fetchData({ url: URLS.reviewCycles, method: "post", body: cycleForm });
        setBusy(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not create the cycle");
            return;
        }
        toast.success(resp?.message || "Created");
        load();
    };

    const act = async (cycle, url, method, body) => {
        if (busy) return;
        setBusy(true);
        const resp = await fetchData({ url, method, body });
        setBusy(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not update the cycle");
            return;
        }
        toast.success(resp?.message || "Done");
        load();
    };

    if (loading) return <SkeletonLoader />;

    return (
        <div className="perf-page">

            {/* ---------- cycles ---------- */}
            <div className="wh-panel">
                <h6 className="wh-section-title" style={{ marginTop: 0 }}>Review Cycles</h6>

                <div className="perf-cycle-form">
                    <TextField select size="small" label="Quarter" value={cycleForm.quarter}
                        onChange={(e) => setCycleForm({ ...cycleForm, quarter: e.target.value })}>
                        {[1, 2, 3, 4].map((q) => <MenuItem key={q} value={q}>Q{q}</MenuItem>)}
                    </TextField>

                    <TextField size="small" label="Year" value={cycleForm.year}
                        onChange={(e) => setCycleForm({ ...cycleForm, year: e.target.value.replace(/[^0-9]/g, "") })} />

                    <Autocomplete
                        multiple size="small" disableCloseOnSelect
                        className="perf-cycle-template"
                        options={templates}
                        getOptionLabel={(t) => t?.name || ""}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        value={templates.filter((t) => cycleForm.templates.includes(t._id))
                            /* keep the admin's order, not the template list's */
                            .sort((a, b) => cycleForm.templates.indexOf(a._id) - cycleForm.templates.indexOf(b._id))}
                        onChange={(e, picked) => setCycleForm({
                            ...cycleForm,
                            templates: picked.map((t) => t._id),
                            template: cycleForm.template || picked[0]?._id || "",
                        })}
                        renderOption={(props, t) => (
                            <li {...props} key={t._id}>
                                <span className="perf-opt">
                                    <b>{t.name}</b>
                                    <em>{t.appliesTo?.length ? t.appliesTo.join(", ") : "Everyone"}</em>
                                </span>
                            </li>
                        )}
                        renderTags={(value, getTagProps) => value.map((t, i) => (
                            <Chip {...getTagProps({ index: i })} key={t._id} size="small"
                                label={`${i + 1}. ${t.name}`} />
                        ))}
                        renderInput={(params) => (
                            <TextField {...params} label="Templates to roll out"
                                placeholder={cycleForm.templates.length ? "" : "Pick one per group"} />
                        )}
                    />

                    <TextField select size="small" label="Everyone else gets"
                        className="perf-cycle-fallback" value={cycleForm.template}
                        onChange={(e) => setCycleForm({ ...cycleForm, template: e.target.value })}>
                        {templates.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                    </TextField>

                    <Button variant="contained" startIcon={<AddRoundedIcon />}
                        disabled={busy || !templates.length} onClick={saveCycle}>
                        Create cycle
                    </Button>
                </div>

                {cycleForm.templates.length > 1 && (
                    <div className="perf-order">
                        <span className="perf-order-note">
                            Tried in this order — the first group somebody fits is the one they are reviewed against
                        </span>
                        <div className="perf-order-list">
                            {cycleForm.templates.map((id, i) => {
                                const t = templates.find((x) => x._id === id);
                                if (!t) return null;
                                return (
                                    <span className="perf-order-item" key={id}>
                                        <b>{i + 1}</b>
                                        <span>
                                            {t.name}
                                            <em>{t.appliesTo?.length ? t.appliesTo.join(", ") : "Everyone"}</em>
                                        </span>
                                        <Tooltip title="Move up" placement="top" arrow>
                                            <span>
                                                <IconButton size="small" disabled={i === 0}
                                                    onClick={() => moveTemplate(i, -1)}>
                                                    <ArrowUpwardRoundedIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Move down" placement="top" arrow>
                                            <span>
                                                <IconButton size="small"
                                                    disabled={i === cycleForm.templates.length - 1}
                                                    onClick={() => moveTemplate(i, 1)}>
                                                    <ArrowDownwardRoundedIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!templates.length && (
                    <p className="wh-section-empty">Build a template below before opening a cycle.</p>
                )}

                {cycles.length > 0 && (
                    <div className="table-responsive wh-table-shell" style={{ marginTop: 14 }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th scope="col" className="wh-col-shrink">Cycle</th>
                                    <th scope="col">Template</th>
                                    <th scope="col" className="wh-col-shrink">Status</th>
                                    <th scope="col" className="wh-col-shrink">Progress</th>
                                    <th scope="col" className="wh-col-shrink">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cycles.map((cycle) => {
                                    const meta = CYCLE_STATUS[cycle.status] || CYCLE_STATUS.draft;
                                    return (
                                        <tr key={cycle._id}>
                                            <td className="wh-col-shrink"><b>{cycle.name}</b></td>
                                            <td>
                                                {cycle.templates?.length > 1
                                                    ? <span className="wh-pills">
                                                        {cycle.templates.map((t) => (
                                                            <Tooltip key={t._id} placement="top" arrow
                                                                title={t.appliesTo?.length ? t.appliesTo.join(", ") : "Everyone"}>
                                                                <span className="wh-pill wh-pill-sm">{t.name}</span>
                                                            </Tooltip>
                                                        ))}
                                                    </span>
                                                    : (cycle.template?.name || "--")}
                                            </td>
                                            <td className="wh-col-shrink">
                                                <span className={`wh-pill ${meta.tone}`}>{meta.label}</span>
                                            </td>
                                            <td className="wh-col-shrink">
                                                {cycle.total
                                                    ? `${cycle.done} of ${cycle.total} complete`
                                                    : "Not launched"}
                                            </td>
                                            <td className="wh-col-shrink">
                                                <div className="wh-actions-row">
                                                    <Tooltip title="See who gets which template" placement="top" arrow>
                                                        <span>
                                                            <IconButton disabled={busy}
                                                                aria-label={`Preview the ${cycle.name} rollout`}
                                                                onClick={() => showPreview(cycle)}>
                                                                <VisibilityOutlinedIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>

                                                    {cycle.status === "draft" && (
                                                        <Tooltip title="Open for self assessment" placement="top" arrow>
                                                            <Button variant="outlined" size="small"
                                                                startIcon={<RocketLaunchOutlinedIcon />}
                                                                disabled={busy}
                                                                onClick={() => act(cycle,
                                                                    `${URLS.reviewCycles}/${cycle._id}/launch`, "post")}>
                                                                Launch
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                    {cycle.status === "self" && (
                                                        <Button variant="outlined" size="small" disabled={busy}
                                                            onClick={() => act(cycle,
                                                                `${URLS.reviewCycles}/${cycle._id}/status`, "put",
                                                                { status: "manager" })}>
                                                            Move to managers
                                                        </Button>
                                                    )}
                                                    {cycle.status === "manager" && (
                                                        <Button variant="outlined" size="small" disabled={busy}
                                                            onClick={() => act(cycle,
                                                                `${URLS.reviewCycles}/${cycle._id}/status`, "put",
                                                                { status: "closed" })}>
                                                            Close
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ---------- template builder ---------- */}
            <div className="wh-panel">
                <div className="perf-group-head" style={{ marginBottom: 14 }}>
                    <h6 className="wh-section-title" style={{ margin: 0 }}>
                        {form._id ? `Editing ${form.name || "template"}` : "Build a Template"}
                    </h6>

                    {form._id && (
                        <Button size="small" variant="text" startIcon={<CloseRoundedIcon />}
                            onClick={() => setForm(blankTemplate())}>
                            Start a new one
                        </Button>
                    )}
                    <Tooltip
                        title={totalWeight === 100
                            ? "The weights add up — this template can be saved"
                            : `Category weights must add up to 100%, ${remainingWeight > 0
                                ? `${remainingWeight}% still to allocate`
                                : `${Math.abs(remainingWeight)}% over`}`}
                        placement="top" arrow>
                        <span className={`wh-pill ${totalWeight === 100 ? "wh-pill-good" : "wh-pill-warn"}`}>
                            {totalWeight}% of 100% weighted
                        </span>
                    </Tooltip>

                    {totalWeight !== 100 && (
                        <span className="perf-weight-hint">
                            {remainingWeight > 0
                                ? `${remainingWeight}% left to allocate`
                                : `${Math.abs(remainingWeight)}% over — reduce a category`}
                        </span>
                    )}
                </div>

                <div className="wh-form-grid">
                    <div className="wh-form-full">
                        <TextField fullWidth size="small" label="Template name"
                            placeholder="Engineering — quarterly review"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            helperText=" " />
                    </div>

                    <div className="wh-form-full">
                        <Autocomplete
                            multiple freeSolo size="small"
                            options={audienceOptions}
                            groupBy={(o) => o.kind}
                            getOptionLabel={(o) => (typeof o === "string" ? o : o.value)}
                            value={form.appliesTo}
                            onChange={(e, picked) => setForm({
                                ...form,
                                appliesTo: [...new Set(picked
                                    .map((o) => (typeof o === "string" ? o.trim() : o.value))
                                    .filter(Boolean))],
                            })}
                            renderOption={(props, o) => (
                                <li {...props} key={`${o.kind}-${o.value}`}>
                                    <span className="perf-opt">
                                        <b>{o.value}</b>
                                        <em>{o.count} {o.count === 1 ? "person" : "people"}</em>
                                    </span>
                                </li>
                            )}
                            renderTags={(value, getTagProps) => value.map((v, i) => (
                                <Chip {...getTagProps({ index: i })} key={v} size="small" label={v} />
                            ))}
                            renderInput={(params) => (
                                <TextField {...params} label="Applies to"
                                    placeholder="Engineering, DevOps, QA, HR, IT, Admin…"
                                    helperText={form.appliesTo.length
                                        ? `Matches ${wordMatches(form.appliesTo)} of ${audiences.headcount} people by department or designation`
                                        : `Left empty this template fits everyone (${audiences.headcount} people)`} />
                            )}
                        />
                    </div>
                </div>

                {form.categories.map((category, ci) => (
                    <div className="perf-cat" key={ci}>
                        <div className="perf-cat-head">
                            <span className="perf-cat-no">{ci + 1}</span>

                            <TextField size="small" label="Category"
                                placeholder="Delivery"
                                value={category.name}
                                onChange={(e) => editCategory(ci, { name: e.target.value })} />

                            <TextField size="small" label="Weight %" className="perf-weight"
                                value={category.weight}
                                onChange={(e) => editCategory(ci, { weight: e.target.value.replace(/[^0-9]/g, "") })} />

                            <Tooltip title="Remove category" placement="top" arrow>
                                <span>
                                    <IconButton className="wh-danger"
                                        disabled={form.categories.length === 1}
                                        onClick={() => setForm({
                                            ...form,
                                            categories: form.categories.filter((_, i) => i !== ci),
                                        })}>
                                        <DeleteOutlineRoundedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </div>

                        {category.kras.map((kra, ki) => (
                            <div className="perf-kra-row" key={ki}>
                                <span className="perf-kra-no">{ci + 1}.{ki + 1}</span>

                                <TextField size="small" label="KRA"
                                    placeholder="Ships work on schedule"
                                    value={kra.title}
                                    onChange={(e) => editKra(ci, ki, { title: e.target.value })} />

                                <TextField size="small" label="How it is measured"
                                    placeholder="Sprint commitments met"
                                    value={kra.metric}
                                    onChange={(e) => editKra(ci, ki, { metric: e.target.value })} />

                                <Tooltip title="Remove KRA" placement="top" arrow>
                                    <span>
                                        <IconButton className="wh-danger"
                                            disabled={category.kras.length === 1}
                                            onClick={() => editCategory(ci, {
                                                kras: category.kras.filter((_, i) => i !== ki),
                                            })}>
                                            <DeleteOutlineRoundedIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </div>
                        ))}

                        <Button variant="outlined" size="small" startIcon={<AddRoundedIcon />}
                            onClick={() => editCategory(ci, { kras: [...category.kras, { title: "", metric: "" }] })}>
                            Add KRA
                        </Button>
                    </div>
                ))}

                <div className="perf-actions">
                    <Button variant="outlined" startIcon={<AddRoundedIcon />}
                        onClick={() => setForm({ ...form, categories: [...form.categories, blankCategory()] })}>
                        Add category
                    </Button>
                    <Tooltip
                        title={totalWeight === 100 ? "" : "Category weights must add up to 100% first"}
                        placement="top" arrow>
                        <span>
                            <Button variant="contained" startIcon={<SaveRoundedIcon />}
                                disabled={busy || totalWeight !== 100} onClick={saveTemplate}>
                                Save template
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </div>

            {/* ---------- what exists already ---------- */}
            {templates.length > 0 && (
                <div className="wh-panel">
                    <h6 className="wh-section-title" style={{ marginTop: 0 }}>Existing Templates</h6>
                    <div className="perf-template-list">
                        {templates.map((t) => (
                            <div className="perf-template" key={t._id}>
                                <div>
                                    <b>{t.name}</b>
                                    <em>
                                        {t.categories?.length} categories ·{" "}
                                        {t.categories?.reduce((n, c) => n + (c.kras?.length || 0), 0)} KRAs ·
                                        created {moment(t.createdDate).format("DD MMM YYYY")}
                                    </em>

                                    <span className="perf-template-for">
                                        <GroupsOutlinedIcon />
                                        {t.appliesTo?.length
                                            ? <>
                                                {t.appliesTo.join(", ")}
                                                <b>{wordMatches(t.appliesTo)} people</b>
                                            </>
                                            : <>Everyone <b>{audiences.headcount} people</b></>}
                                    </span>
                                </div>

                                <div className="perf-template-side">
                                    <div className="wh-pills">
                                        {(t.categories || []).map((c, i) => (
                                            <span className="wh-pill" key={i}>{c.name} {c.weight ? `${c.weight}%` : ""}</span>
                                        ))}
                                    </div>

                                    <Tooltip title="Open in the builder" placement="top" arrow>
                                        <Button size="small" variant="outlined"
                                            onClick={() => editTemplate(t)}>Edit</Button>
                                    </Tooltip>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ---------- who would get what ---------- */}
            <Dialog open={!!preview} onClose={() => setPreview(null)}
                maxWidth="md" fullWidth className="wh-dialog perf-preview">
                <DialogTitle>
                    {preview?.cycle?.name} rollout
                    <span className="perf-preview-sub">
                        {preview?.headcount} people · {preview?.rollout?.length || 0} template
                        {preview?.rollout?.length === 1 ? "" : "s"}
                    </span>
                </DialogTitle>

                <DialogContent dividers>
                    {preview?.rollout?.map((group) => (
                        <div className="perf-preview-group" key={group._id}>
                            <div className="perf-preview-head">
                                <b>{group.name}</b>
                                <span className="wh-pill">{group.count} people</span>
                                <span className="wh-pill wh-pill-quiet">{group.kras} KRAs</span>
                                <em>{group.appliesTo?.length ? group.appliesTo.join(", ") : "Everyone"}</em>
                            </div>

                            <div className="perf-preview-people">
                                {group.people.map((p) => (
                                    <span className="perf-preview-person" key={p.employeeCode}>
                                        {p.employeeName}
                                        <em>{p.designation || p.department || p.employeeCode}</em>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* nobody should be left out — if they are, say exactly who */}
                    {preview?.unmatched?.length > 0 && (
                        <div className="perf-preview-group perf-preview-group--miss">
                            <div className="perf-preview-head">
                                <b>Not covered by any template</b>
                                <span className="wh-pill wh-pill-warn">{preview.unmatched.length} people</span>
                                <em>they get no review until a template matches them</em>
                            </div>

                            <div className="perf-preview-people">
                                {preview.unmatched.map((p) => (
                                    <span className="perf-preview-person" key={p.employeeCode}>
                                        {p.employeeName}
                                        <em>{p.designation || p.department || p.employeeCode}</em>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button variant="outlined" onClick={() => setPreview(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Configure;
